import React, { Component } from 'react';
import { Modal } from 'react-bootstrap';
import produce from 'immer';
import isEqual from 'lodash/isEqual';
import styles from './OutletAssignmentModal.css';
import OutletAssignmentConfirmationModal from './OutletAssignmentConfirmationModal';

class OutletAssignmentModal extends Component {
  state = {
    changes: new Map(),
    showComfirmation: false,
    searchTerm: '',
  };

  componentDidMount() {
    const { onLoad } = this.props;
    onLoad();
  }

  handleAssignButtonClick = () => {
    this.setState({
      showComfirmation: true,
    });
  };

  handleCancelButtonClick = () => {
    const { onCancel } = this.props;
    this.setState({
      changes: new Map(),
    });

    onCancel();
  };

  handleSelectionChange = (outletId, toInclude) => {
    const { selectedIds } = this.props;
    const { changes } = this.state;
    const isOriginallySelected = selectedIds.includes(outletId);

    const newChanges = produce(changes, draft => {
      if (!isOriginallySelected && toInclude) {
        draft.set(outletId, 'add');
      } else if (isOriginallySelected && !toInclude) {
        draft.set(outletId, 'delete');
      } else {
        draft.delete(outletId);
      }
    });

    this.setState({ changes: newChanges });
  };

  handleSelectAllChange = event => {
    const toInclude = event.target.checked;
    const { outlets, selectedIds } = this.props;
    const { changes, searchTerm } = this.state;

    // Select all/none applies only to the filtered list
    const isSelectedSet = new Set(selectedIds);
    const filteredOutlets = outlets.filter(({ name }) =>
      name.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );

    const newChanges = produce(changes, draft => {
      for (const { id } of filteredOutlets) {
        const isOriginallySelected = isSelectedSet.has(id);

        if (!isOriginallySelected && toInclude) {
          draft.set(id, 'add');
        } else if (isOriginallySelected && !toInclude) {
          draft.set(id, 'delete');
        } else {
          draft.delete(id);
        }
      }
    });

    this.setState({ changes: newChanges });
  };

  handleAssignConfirm = () => {
    const { onConfirm } = this.props;
    const finalSelections = this.getSelections();
    onConfirm(finalSelections);
  };

  handleAssignCancel = () => {
    this.setState({
      showComfirmation: false,
    });
  };

  handleSearchFieldChange = event => {
    this.setState({
      searchTerm: event.target.value,
    });
  };

  getSelections = () => {
    const { selectedIds } = this.props;
    const { changes } = this.state;

    const selections = selectedIds.filter(id => changes.get(id) !== 'delete');

    for (const [id, action] of changes) {
      if (action === 'add') {
        selections.push(id);
      }
    }

    return selections;
  };

  render() {
    const {
      isShowing,
      onCancel,
      isOutletsLoaded,
      outlets,
      configurationName,
      selectedIds,
    } = this.props;
    const { showComfirmation, searchTerm } = this.state;

    if (!isOutletsLoaded) {
      return null;
    }

    const filteredOutlets = outlets.filter(({ name, paymentConfiguration }) => {
      const haystacks = [
        name.toLowerCase(),
        paymentConfiguration.name.toLowerCase(),
      ];
      const needle = searchTerm.trim().toLowerCase();

      return haystacks.some(haystack => haystack.includes(needle));
    });

    if (showComfirmation) {
      return (
        <OutletAssignmentConfirmationModal
          isShowing
          configurationName={configurationName}
          onProceed={this.handleAssignConfirm}
          onCancel={this.handleAssignCancel}
        />
      );
    }

    const finalSelections = this.getSelections();
    const isSelectedSet = new Set(finalSelections);
    const isAllSelected = filteredOutlets.every(({ id }) =>
      isSelectedSet.has(id)
    );
    const isPartiallySelected =
      !isAllSelected && filteredOutlets.some(({ id }) => isSelectedSet.has(id));

    // This determines if we enable the "Assign" button
    const canSubmitSelections = !isEqual(finalSelections, selectedIds);

    return (
      <Modal show={isShowing}>
        <Modal.Header>
          <Modal.Title>Assign Outlet</Modal.Title>
        </Modal.Header>

        <Modal.Body className={styles.body}>
          <input
            type="text"
            placeholder="Search"
            className="form-control"
            value={searchTerm}
            onChange={this.handleSearchFieldChange}
          />

          <table className={styles.outletTable}>
            <thead>
              <tr>
                <th className={styles.checkboxCol}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={this.handleSelectAllChange}
                    ref={el => {
                      // Set `indeterminate` state when it is partially selected
                      if (el) {
                        el.indeterminate = isPartiallySelected;
                      }
                    }}
                  />
                </th>
                <th>Select All</th>
                <th>Current Configuration</th>
              </tr>
            </thead>

            <tbody>
              {filteredOutlets.map(outlet => {
                const { id, name, paymentConfiguration } = outlet;
                const isSelected = isSelectedSet.has(id);

                return (
                  <tr key={id}>
                    <td className={styles.checkboxCol}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={e =>
                          this.handleSelectionChange(id, e.target.checked)
                        }
                      />
                    </td>
                    <td>{name}</td>
                    <td>{paymentConfiguration.name}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Modal.Body>

        <div className={styles.footer}>
          <div>
            Selected: <strong>{finalSelections.length || 'None'}</strong>
          </div>

          <div>
            <button className="btn btn-default" onClick={onCancel}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={this.handleAssignButtonClick}
              disabled={!canSubmitSelections}
            >
              Assign
            </button>
          </div>
        </div>
      </Modal>
    );
  }
}

OutletAssignmentModal.defaultProps = {
  configurationName: '',
  selectedIds: [],
  isOutletsLoaded: false,
  outlets: [],
  onCancel: () => {},
  onConfirm: () => {},
  isShowing: false,
};

export default OutletAssignmentModal;
